import React, { useEffect, useState, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutGrid, CalendarClock, BookOpen, UserRound, Star, Users, Eye, RotateCcw } from "lucide-react";
import Emblem from "@/components/Emblem";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const ROLE_LABEL = { recruit: "Recruit", family: "Family", recruiter: "Recruiter", admin: "Admin" };

function navFor(role) {
  const base = [
    { to: "/", label: "Command", icon: LayoutGrid, testid: "nav-command", end: true },
    { to: "/training", label: "Training", icon: CalendarClock, testid: "nav-training" },
    { to: "/resources", label: "Resources", icon: BookOpen, testid: "nav-resources" },
  ];
  if (role === "recruit" || role === "family") {
    base.push({ to: "/leadership", label: "Leadership", icon: Star, testid: "nav-leadership" });
  }
  if (role === "recruiter" || role === "admin") {
    base.push({ to: "/recruits", label: "Recruits", icon: Users, testid: "nav-recruits" });
  }
  base.push({ to: "/profile", label: "Profile", icon: UserRound, testid: "nav-profile" });
  return base;
}

export default function AppShell({ children }) {
  const { effectiveRole, isAdmin, viewAs, setViewAs } = useAuth();
  const navigate = useNavigate();
  const items = navFor(effectiveRole);
  const [unread, setUnread] = useState(0);
  const impersonating = isAdmin && !!viewAs && viewAs !== "admin";

  const refreshUnread = useCallback(() => {
    if (effectiveRole !== "recruit") { setUnread(0); return; }
    api.get("/me/messages/unread-count").then((r) => setUnread(r.data.count || 0)).catch(() => {});
  }, [effectiveRole]);

  useEffect(() => {
    refreshUnread();
    if (effectiveRole !== "recruit") return;
    const iv = setInterval(refreshUnread, 30000);
    const onRead = () => refreshUnread();
    window.addEventListener("messages-read", onRead);
    return () => { clearInterval(iv); window.removeEventListener("messages-read", onRead); };
  }, [effectiveRole, refreshUnread]);

  return (
    <div className="min-h-screen bg-[var(--rsp-bg)] flex justify-center">
      <div className="w-full max-w-[720px] flex flex-col min-h-screen relative">
        {/* Header */}
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-[var(--rsp-bg)]/85 border-b border-[var(--rsp-border)]">
          <div className="flex items-center justify-between px-5 py-3">
            <div className="flex items-center gap-3">
              <Emblem size={44} glow={false} />
              <div className="leading-tight">
                <div className="eyebrow gold" style={{ letterSpacing: "0.16em" }}>RSP · Riverside</div>
                <div className="heading text-2xl -mt-0.5">RSP</div>
              </div>
            </div>
            <button
              data-testid="role-pill-btn"
              onClick={() => isAdmin && navigate("/admin")}
              className="pill"
              title={isAdmin ? "Open Admin CMS" : ""}
            >
              {effectiveRole === "admin" ? <Users size={15} /> : <UserRound size={15} />}
              {ROLE_LABEL[effectiveRole] || "Member"}
            </button>
          </div>
        </header>

        {/* View-As impersonation banner: lets an admin jump back to their real view */}
        {impersonating && (
          <div data-testid="viewas-banner"
            className="sticky top-[60px] z-20 flex items-center gap-2 px-5 py-2.5 border-b border-[var(--rsp-gold)]/40 bg-[var(--rsp-gold)]/10 backdrop-blur-xl">
            <Eye size={15} className="gold shrink-0" />
            <span className="text-xs text-[var(--rsp-cream)] flex-1 min-w-0">
              Viewing as <span className="gold font-head uppercase tracking-wide">{ROLE_LABEL[viewAs] || viewAs}</span>
            </span>
            <button data-testid="return-to-admin-btn"
              onClick={() => { setViewAs(null); navigate("/profile"); }}
              className="shrink-0 flex items-center gap-1.5 text-xs font-head uppercase tracking-wide px-3 py-1.5 rounded-full bg-[var(--rsp-gold)] text-black hover:opacity-90 transition-opacity">
              <RotateCcw size={13} /> Return to Admin
            </button>
          </div>
        )}

        {/* Content */}
        <main className="flex-1 px-5 py-5 pb-28 rsp-scroll">{children}</main>

        {/* Bottom nav */}
        <nav className="fixed bottom-0 z-30 w-full max-w-[720px] border-t border-[var(--rsp-border)] bg-[var(--rsp-bg)]/95 backdrop-blur-xl">
          <div className="flex items-stretch justify-around px-2 py-2">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `relative flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-colors ${
                    isActive ? "text-[var(--rsp-gold)]" : "text-[var(--rsp-muted)] hover:text-[var(--rsp-cream)]"
                  }`
                }
              >
                <it.icon size={22} />
                {it.testid === "nav-command" && unread > 0 && (
                  <span data-testid="command-unread-badge"
                    className="absolute -top-0.5 right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--rsp-gold)] text-black text-[10px] font-bold flex items-center justify-center">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
                <span className="font-head uppercase text-[11px] tracking-wider">{it.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
}
